import { FieldPosition, LiveGameEvent, LiveMatchState, LivePlayer } from '../liveMatchTypes';

// ─── Costanti ────────────────────────────────────────────────────────────────

const MAX_COLS = 34; // celle massime per griglia punteggio (copre set fino a 34-32)

// ─── Helpers dati ────────────────────────────────────────────────────────────

function shirtOf(id: number | undefined, callup: LivePlayer[]): string {
  if (id === undefined) return '–';
  const p = callup.find((pl) => pl.id === id);
  return p?.shirtNumber ? String(p.shirtNumber) : '–';
}

function formationOf(lineup: FieldPosition[], callup: LivePlayer[]): string {
  return [1, 2, 3, 4, 5, 6]
    .map((pos) => {
      const fp = lineup.find((f) => f.position === pos);
      return fp ? shirtOf(fp.playerId, callup) : '–';
    })
    .join('&nbsp;&nbsp;');
}

/** Lineup INIZIALE del set: snapshot del primo evento del set (prima di qualunque cambio). */
function startingLineup(
  setNumber: number,
  log: LiveGameEvent[],
  side: 'home' | 'away',
  fallback: FieldPosition[],
): FieldPosition[] {
  const first = log.find((e) => e.setNumber === setNumber);
  if (!first) return fallback;
  return side === 'home' ? first.snapshot.homeLineup : first.snapshot.awayLineup;
}

function subsOf(events: LiveGameEvent[], callup: LivePlayer[]): string {
  if (!events.length) return '—';
  return events
    .map((e) => `${shirtOf(e.playerId, callup)}&rarr;${shirtOf(e.substituteId, callup)}`)
    .join('&nbsp;&nbsp;');
}

function toBoxes(used: number): string {
  return [0, 1]
    .map(
      (i) =>
        `<span style="display:inline-block;width:11px;height:11px;border:1.5px solid #000;`
        + `text-align:center;line-height:9px;font-size:8pt;margin:0 1px;vertical-align:middle">`
        + `${i < used ? '&times;' : '&nbsp;'}</span>`,
    )
    .join('');
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

// ─── Griglia punteggio ────────────────────────────────────────────────────────

/** Riga di celle: cella N è marcata se il team ha raggiunto quel punteggio. */
function scoreRow(score: number, colorClass: string): string {
  return Array.from({ length: MAX_COLS }, (_, i) => {
    const n = i + 1;
    const marked = n <= score;
    return `<td class="gc${marked ? ` ${colorClass}` : ''}">${marked ? '&times;' : ''}</td>`;
  }).join('');
}

/** Riga milestone con etichette ai multipli di 5 e ogni cella dal 26 in poi. */
function milestoneRow(): string {
  return Array.from({ length: MAX_COLS }, (_, i) => {
    const n = i + 1;
    const lbl = n % 5 === 0 || n >= 26 ? String(n) : '';
    return `<td class="gm">${lbl}</td>`;
  }).join('');
}

// ─── Blocco set ───────────────────────────────────────────────────────────────

function setBlock(
  setNumber: number,
  homePoints: number,
  awayPoints: number,
  homeLineupFinal: FieldPosition[],
  awayLineupFinal: FieldPosition[],
  log: LiveGameEvent[],
  homeCallup: LivePlayer[],
  awayCallup: LivePlayer[],
  homeLiberoId: number | null,
  awayLiberoId: number | null,
  played: boolean,
): string {
  const hLineup = played ? startingLineup(setNumber, log, 'home', homeLineupFinal) : [];
  const aLineup = played ? startingLineup(setNumber, log, 'away', awayLineupFinal) : [];

  const hForm = played ? formationOf(hLineup, homeCallup) : '';
  const aForm = played ? formationOf(aLineup, awayCallup) : '';

  const hSubEvents = log.filter((e) => e.type === 'SUBSTITUTION_HOME' && e.setNumber === setNumber);
  const aSubEvents = log.filter((e) => e.type === 'SUBSTITUTION_AWAY' && e.setNumber === setNumber);
  const hTO = log.filter((e) => e.type === 'TIMEOUT_HOME' && e.setNumber === setNumber).length;
  const aTO = log.filter((e) => e.type === 'TIMEOUT_AWAY' && e.setNumber === setNumber).length;

  const hLib = homeLiberoId ? shirtOf(homeLiberoId, homeCallup) : '—';
  const aLib = awayLiberoId ? shirtOf(awayLiberoId, awayCallup) : '—';

  const hSubs = played ? subsOf(hSubEvents, homeCallup) : '—';
  const aSubs = played ? subsOf(aSubEvents, awayCallup) : '—';

  const emptyGrid = Array.from({ length: MAX_COLS }, () => '<td class="gc"></td>').join('');

  return `
<table class="st">
  <colgroup>
    <col class="col-sn">
    <col class="col-info">
    ${Array.from({ length: MAX_COLS }, () => '<col>').join('')}
    <col class="col-score">
  </colgroup>
  <tbody>
    <tr class="team-row">
      <td class="sn-cell" rowspan="3">${setNumber}&deg;<br>SET</td>
      <td class="info">
        <div class="info-line1"><b>A</b>&nbsp;&nbsp;<span class="lbl">Form.:</span>&nbsp;<b>${hForm}</b></div>
        <div class="info-line2"><span class="lbl">Sost.:</span>&nbsp;${hSubs}&nbsp;&nbsp;<span class="lbl">Lib.:</span>&nbsp;<b>${hLib}</b>&nbsp;&nbsp;<span class="lbl">T:</span>&nbsp;${toBoxes(hTO)}</div>
      </td>
      ${played ? scoreRow(homePoints, 'ga') : emptyGrid}
      <td class="score">${played ? homePoints : ''}</td>
    </tr>
    <tr class="team-row">
      <td class="info">
        <div class="info-line1"><b>B</b>&nbsp;&nbsp;<span class="lbl">Form.:</span>&nbsp;<b>${aForm}</b></div>
        <div class="info-line2"><span class="lbl">Sost.:</span>&nbsp;${aSubs}&nbsp;&nbsp;<span class="lbl">Lib.:</span>&nbsp;<b>${aLib}</b>&nbsp;&nbsp;<span class="lbl">T:</span>&nbsp;${toBoxes(aTO)}</div>
      </td>
      ${played ? scoreRow(awayPoints, 'gb') : emptyGrid}
      <td class="score">${played ? awayPoints : ''}</td>
    </tr>
    <tr class="ms-row">
      <td></td>
      ${milestoneRow()}
      <td></td>
    </tr>
  </tbody>
</table>`;
}

// ─── HTML completo ────────────────────────────────────────────────────────────

function buildHTML(state: LiveMatchState): string {
  const {
    homeTeamName, awayTeamName, eventDate,
    completedSets, eventLog,
    homeCallup, awayCallup,
    setsWon, homeLiberoId, awayLiberoId,
    strategy,
  } = state;

  const maxSets = strategy === 'BEST_OF_3' ? 3 : 5;
  const dateStr = formatDate(eventDate);

  const setsHTML = Array.from({ length: maxSets }, (_, i) => {
    const n = i + 1;
    const s = completedSets.find((cs) => cs.setNumber === n);
    return setBlock(
      n,
      s?.homePoints ?? 0,
      s?.awayPoints ?? 0,
      s?.homeLineup ?? [],
      s?.awayLineup ?? [],
      eventLog,
      homeCallup,
      awayCallup,
      homeLiberoId,
      awayLiberoId,
      !!s,
    );
  }).join('\n');

  return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8">
  <title>Referto — ${homeTeamName} vs ${awayTeamName}</title>
  <style>
    @page { size: A4 landscape; margin: 8mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 8pt;
      color: #000;
      background: #fff;
      width: 281mm;
    }

    /* ── Intestazione ── */
    .hdr {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border: 2px solid #000;
      padding: 2.5mm 4mm;
      margin-bottom: 2.5mm;
    }
    .hdr-title { font-size: 15pt; font-weight: 900; }
    .hdr-sub   { font-size: 7pt; color: #666; margin-top: 0.5mm; }
    .hdr-date  { text-align: center; }
    .hdr-date .dl { font-size: 7pt; color: #555; }
    .hdr-date .dv { font-size: 12pt; font-weight: bold; margin-top: 0.5mm; }
    .hdr-teams { text-align: right; }
    .tr { display: flex; align-items: baseline; gap: 3mm; margin-bottom: 1mm; }
    .sl { font-size: 7.5pt; color: #555; white-space: nowrap; }
    .sn { font-size: 11pt; font-weight: bold; border-bottom: 1px solid #000; min-width: 65mm; }

    /* ── Tabella set ── */
    .st {
      width: 100%;
      table-layout: fixed;
      border-collapse: collapse;
      margin-bottom: 1.5mm;
      break-inside: avoid;
    }
    .col-sn    { width: 10mm; }
    .col-info  { width: 66mm; }
    .col-score { width: 13mm; }

    /* Numero set */
    .sn-cell {
      font-weight: 900;
      font-size: 8pt;
      text-align: center;
      vertical-align: middle;
      writing-mode: vertical-lr;
      transform: rotate(180deg);
      border: 1px solid #888;
      background: #e8e8e8;
      padding: 1mm;
    }

    /* Colonna info squadra */
    .info {
      border: 0.5px solid #aaa;
      vertical-align: middle;
      padding: 1mm 1.5mm;
      font-size: 7.5pt;
    }
    .info-line1 { margin-bottom: 1px; white-space: nowrap; overflow: hidden; }
    .info-line2 { white-space: nowrap; overflow: hidden; color: #222; }
    .lbl { color: #666; font-size: 6.5pt; }

    /* Celle griglia */
    .gc {
      border: 0.5px solid #ccc;
      text-align: center;
      vertical-align: middle;
      font-size: 7pt;
      font-weight: bold;
      padding: 0;
    }
    .ga { background: #dbeafe; } /* blu chiaro — squadra A */
    .gb { background: #fce7f3; } /* rosa chiaro — squadra B */

    /* Altezze righe */
    .team-row { height: 8.5mm; }
    .ms-row   { height: 3.5mm; }

    /* Punteggio finale */
    .score {
      border: 0.5px solid #aaa;
      text-align: center;
      vertical-align: middle;
      font-size: 14pt;
      font-weight: 900;
    }

    /* Milestone numbers */
    .gm {
      text-align: center;
      font-size: 5.5pt;
      color: #777;
      padding: 0;
      vertical-align: top;
    }

    /* ── Footer ── */
    .ftr {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      border: 1.5px solid #000;
      padding: 2.5mm 4mm;
      margin-top: 2mm;
    }
    .result { font-size: 10pt; line-height: 1.6; }
    .result b { font-size: 15pt; }
    .sig { flex: 1; text-align: center; margin: 0 5mm; }
    .sig-line {
      border-top: 1px solid #000;
      margin-top: 8mm;
      padding-top: 1mm;
      font-size: 6.5pt;
      color: #555;
    }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .st  { break-inside: avoid; }
    }
  </style>
</head>
<body>

  <div class="hdr">
    <div>
      <div class="hdr-title">Rapporto Partita</div>
      <div class="hdr-sub">Volley Amatoriale</div>
    </div>
    <div class="hdr-date">
      <div class="dl">Data</div>
      <div class="dv">${dateStr}</div>
    </div>
    <div class="hdr-teams">
      <div class="tr"><span class="sl">Squadra A:</span><span class="sn">${homeTeamName}</span></div>
      <div class="tr"><span class="sl">Squadra B:</span><span class="sn">${awayTeamName}</span></div>
    </div>
  </div>

  ${setsHTML}

  <div class="ftr">
    <div class="result">
      RISULTATO FINALE<br>
      Set Squadra A:&nbsp;<b>${setsWon.home}</b><br>
      Set Squadra B:&nbsp;<b>${setsWon.away}</b>
    </div>
    <div class="sig">
      <div class="sig-line">Firma Resp. Squadra A</div>
    </div>
    <div class="sig">
      <div class="sig-line">Firma Resp. Squadra B</div>
    </div>
  </div>

  <script>
    window.addEventListener('load', function () {
      window.print();
    });
  </script>
</body>
</html>`;
}

// ─── Export pubblico ──────────────────────────────────────────────────────────

export function printMatchReport(state: LiveMatchState): void {
  const html = buildHTML(state);
  const win = window.open('', '_blank');
  if (!win) {
    // eslint-disable-next-line no-alert
    alert('Impossibile aprire la finestra di stampa.\nVerifica che i popup non siano bloccati per questo sito.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}
